import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Spinner, Tag, TextInput } from '@opendate-app/setlist';
import axios from 'axios';
import { SeatsioEventManager, SeatsioSeatingChart } from '@seatsio/seatsio-react';
import { toastError } from '../../../shared/toastHelper';

const SelectedList = ({ selected, selectedAreas, onRemoveLabel, onRemoveArea }) => (
  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
    {(selected || []).map(label => (
      <Tag key={label} size="sm" onDismiss={() => onRemoveLabel(label)}>{label}</Tag>
    ))}
    {Object.entries(selectedAreas || {}).map(([label, count]) => (
      <Tag key={`ga-${label}`} size="sm" onDismiss={() => onRemoveArea(label)}>
        {label} (x{Number(count) || 0})
      </Tag>
    ))}
  </div>
);

const KillModal = ({
  open,
  onClose,
  title,
  seatsIOPublicKey,
  editorKeyUrl,
  channels,
  selectedEvent,
  initialKill,
  onSubmit,
  takenObjects
}) => {
  const chartRef = useRef(null);
  const takenRef = useRef(takenObjects);
  takenRef.current = takenObjects;
  const [takenNotice, setTakenNotice] = useState(null);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [secretKey, setSecretKey] = useState(null);
  const [keyFailed, setKeyFailed] = useState(false);

  useEffect(() => {
    if (!open || !editorKeyUrl) return undefined;
    let active = true;
    setSecretKey(null);
    setKeyFailed(false);
    const fail = () => {
      if (!active) return;
      setKeyFailed(true);
      toastError('The seat map could not be opened. Try again shortly.');
    };
    axios.get(editorKeyUrl)
      .then(({ data }) => { if (!active) return; if (data && data.secretKey) { setSecretKey(data.secretKey); } else { fail(); } })
      .catch(fail);
    return () => { active = false; };
  }, [open, editorKeyUrl]);

  useEffect(() => {
    if (!open) return;
    setName(initialKill?.name || '');
    setSelected(initialKill?.objects || []);
    setSelectedAreas(initialKill?.area_places || {});
    setTakenNotice(null);
  }, [open, initialKill?.name, JSON.stringify(initialKill?.objects || []), JSON.stringify(initialKill?.area_places || {})]);

  const totalSelected = (selected.length + Object.values(selectedAreas || {}).reduce((a, b) => a + (Number(b) || 0), 0));
  const isSaveDisabled = isSaving || !name || (selected.length === 0 && Object.keys(selectedAreas || {}).length === 0);

  const mapProps = {
    selectableObjects: initialKill?.objects || [],
    ...(takenObjects && {
      extraConfig: { taken: takenObjects },
      objectColor: (object, defaultColor, extraConfig) => {
        const taken = extraConfig && extraConfig.taken && extraConfig.taken[object.label];
        return taken ? taken.color : defaultColor;
      },
    }),
    onRenderStarted: chart => {
      chartRef.current = chart;
      const isVariableGA = (obj) => obj && obj.objectType === 'GeneralAdmissionArea' && !obj.bookAsAWhole;
      chart.config.onObjectSelected = (object) => {
        const taken = takenRef.current && takenRef.current[object.label];
        if (taken) {
          chart.deselectObjects([object.label]);
          setTakenNotice(`${object.label} is already in ${taken.owner}.`);
          return;
        }
        setTakenNotice(null);
        if (isVariableGA(object)) {
          const count = Number(object.numSelected || 0);
          setSelectedAreas(prev => ({ ...(prev || {}), [object.label]: count }));
        } else {
          setSelected(prev => Array.from(new Set([...(prev || []), object.label])));
        }
      };
      chart.config.onObjectDeselected = (object) => {
        if (isVariableGA(object)) {
          setSelectedAreas(prev => {
            const copy = { ...(prev || {}) };
            delete copy[object.label];
            return copy;
          });
        } else {
          setSelected(prev => (prev || []).filter(l => l !== object.label));
        }
      };
      if ((initialKill?.objects || []).length > 0) {
        Promise.resolve().then(() => chart.selectObjects(initialKill.objects)).catch(() => null);
      }
    },
    region: 'na'
  };

  const handleSave = () => {
    if (isSaveDisabled) return;
    setIsSaving(true);
    onSubmit({ name, objects: selected, area_places: selectedAreas })
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal open={open} onClose={onClose} title={title} width="95vw" data-above-sidebar>
      <div className="d-flex flex-column flex-md-row" style={{ width: '100%', height: 'calc(95vh - 110px)', minHeight: 0 }}>
        <div className="flex-fill d-flex" style={{ minWidth: 0, minHeight: 0, height: '100%' }}>
          <div className="w-100 h-100">
            {editorKeyUrl ? (
              secretKey ? (
                <SeatsioEventManager
                  secretKey={secretKey}
                  event={selectedEvent && selectedEvent.seatsio_event_id}
                  mode="select"
                  unavailableObjectsSelectable={false}
                  {...mapProps}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  {keyFailed ? 'The seat map could not be opened.' : <Spinner aria-label="Loading seat map" />}
                </div>
              )
            ) : (
              <SeatsioSeatingChart
                workspaceKey={seatsIOPublicKey}
                channels={channels}
                event={selectedEvent && selectedEvent.seatsio_event_id}
                {...mapProps}
              />
            )}
          </div>
        </div>
        <div className="pl-md-4 pt-3 pt-md-0 d-flex flex-column h-100" style={{ width: '100%', maxWidth: 450 }}>
          <div className="mb-3">
            <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--sl-text-primary)' }}>Basic Details</div>
            <div style={{ fontSize: '14px', color: 'var(--sl-text-tertiary)' }}>Essential information regarding this kill.</div>
          </div>
          <div className="mb-3">
            <TextInput label="Kill Name" required value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="mb-2" style={{ fontSize: '12px', color: 'var(--sl-text-tertiary)' }}>
            Selected ({totalSelected})
          </div>
          {takenNotice && (
            <div className="mb-2" role="status" style={{ fontSize: '13px', color: 'var(--sl-text-error, #d92d20)' }}>
              {takenNotice}
            </div>
          )}
          <div className="flex-grow-1 overflow-auto" style={{ background: 'var(--sl-bg-secondary)', borderRadius: 'var(--sl-radius-md)', padding: 16 }}>
            <SelectedList
              selected={selected}
              selectedAreas={selectedAreas}
              onRemoveLabel={(label) => {
                setSelected(prev => (prev || []).filter(l => l !== label));
                const chart = chartRef.current;
                chart.deselectObjects([label]);
              }}
              onRemoveArea={(label) => {
                setSelectedAreas(prev => {
                  const copy = { ...(prev || {}) };
                  delete copy[label];
                  return copy;
                });
                const chart = chartRef.current;
                chart.deselectObjects([label]);
              }}
            />
          </div>
          <div className="d-flex justify-content-end mt-3" style={{ gap: 12 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={isSaveDisabled} onClick={() => handleSave()}>
              {isSaving ? <Spinner size="sm" color="currentColor" aria-label="Saving" /> : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

KillModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  seatsIOPublicKey: PropTypes.string,
  editorKeyUrl: PropTypes.string,
  channels: PropTypes.arrayOf(PropTypes.string),
  selectedEvent: PropTypes.object,
  initialKill: PropTypes.shape({
    name: PropTypes.string,
    objects: PropTypes.array,
    area_places: PropTypes.object
  }),
  onSubmit: PropTypes.func.isRequired,
  takenObjects: PropTypes.objectOf(PropTypes.shape({ owner: PropTypes.string, color: PropTypes.string }))
};

KillModal.defaultProps = {
  seatsIOPublicKey: '',
  editorKeyUrl: null,
  channels: ['NO_CHANNEL'],
  selectedEvent: null,
  initialKill: null,
  takenObjects: null
};

export default KillModal;


